import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, CircularProgress } from '@material-ui/core'
import { ezScheduleDetails } from '../../../../../models/WorkOrder/types'
import { useExistingDataGateStyles } from '../styles'

interface Props {
    ezSchedule: ezScheduleDetails
    phaseId: number
    onReset: () => Promise<unknown>
    onExit: () => void
    children: React.ReactNode
}

const phaseHasExistingData = (
    ezSchedule: ezScheduleDetails,
    phaseId: number,
): boolean => {
    const phase = ezSchedule?.phases?.find((p) => p.phase.id === phaseId)
    if (!phase) return false

    return (
        phase.work_start_date != null ||
        phase.work_end_date != null ||
        phase.template_services.length > 0 ||
        phase.template_units.some((tu) => tu.is_included)
    )
}

export const ExistingDataGate: React.FC<Props> = ({
    ezSchedule,
    phaseId,
    onReset,
    onExit,
    children,
}) => {
    const classes = useExistingDataGateStyles()
    const [showPrompt, setShowPrompt] = useState(false)
    const [resetting, setResetting] = useState(false)

    useEffect(() => {
        setShowPrompt(phaseHasExistingData(ezSchedule, phaseId))
    }, [])

    const handleReset = async () => {
        setResetting(true)
        try {
            await onReset()
            setShowPrompt(false)
        } finally {
            setResetting(false)
        }
    }

    if (!showPrompt) {
        return <>{children}</>
    }

    return (
        <Box className={classes.wrapper}>
            <Box className={classes.card}>
                <Typography className={classes.title}>
                    Existing Schedule Data Found
                </Typography>
                <Typography className={classes.subtitle}>
                    This schedule already has data configured. If you wish to
                    use Lori to setup your project, you need to clear the
                    existing data and start fresh. If you would like to edit
                    your existing schedule, instead choose the {`"`}Go Back{`"`}{' '}
                    option and then use the Auto Scheduler. Would you like to
                    clear the existing data and start fresh, or go back?
                </Typography>
                <Box className={classes.actions}>
                    <Button
                        className={classes.exitButton}
                        onClick={onExit}
                        disabled={resetting}
                        variant="outlined"
                    >
                        Go Back
                    </Button>
                    <Button
                        className={classes.resetButton}
                        onClick={handleReset}
                        disabled={resetting}
                        variant="contained"
                    >
                        {resetting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Start Fresh'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
